// src/crm/components/DealForm.tsx
import { useState, useRef } from 'react'
import type { Campaign, Deal, DealStage, CompModel, Attachment } from '../../lib/types'
import type { CreateDealInput } from '../hooks/useDeals'
import { uploadAttachment } from '../hooks/useAttachments'

export const DEAL_STAGE_LABELS: Record<DealStage, string> = {
  prospecte: 'Prospecté',
  contacte: 'Contacté',
  en_discussion: 'En discussion',
  accepte: 'Accepté',
  brief_envoye: 'Brief envoyé',
  contenu_recu: 'Contenu reçu',
  publie: 'Publié',
  paye_cloture: 'Payé / Clôturé',
  decline_archive: 'Décliné / Archivé',
}

const DEAL_STAGES: DealStage[] = [
  'prospecte',
  'contacte',
  'en_discussion',
  'accepte',
  'brief_envoye',
  'contenu_recu',
  'publie',
  'paye_cloture',
  'decline_archive',
]

const COMP_MODEL_LABELS: Record<CompModel, string> = {
  flat: 'Forfait fixe',
  gifting: 'Gifting',
  promo_affiliate: 'Code promo / Affiliation',
  hybrid: 'Hybride',
}

/** Sous-type de CreateDealInput avec creatorId et appId obligatoires (contexte formulaire) */
type DealPayload = Required<Pick<CreateDealInput, 'creatorId' | 'appId' | 'stage' | 'currency'>> &
  Pick<CreateDealInput, 'campaignId' | 'compModel' | 'amount' | 'promoCode' | 'affiliateRate' | 'dueDate'>

type Props = {
  creatorId: string
  appId: string
  campaigns: Campaign[]
  initial?: Deal
  dealId?: string           // ← ajout : id du deal existant, permet l'upload de brief
  onSave: (payload: DealPayload) => void
  onCancel: () => void
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  background: 'var(--elevated)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  padding: '0.375rem 0.5rem',
  fontFamily: 'inherit',
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  fontSize: '0.8125rem',
  color: 'var(--muted)',
  fontWeight: 500,
}

export function DealForm({ creatorId, appId, campaigns, initial, dealId, onSave, onCancel }: Props) {
  const [stage, setStage] = useState<DealStage>(initial?.stage ?? 'prospecte')
  const [compModel, setCompModel] = useState<CompModel | ''>(initial?.compModel ?? '')
  const [amount, setAmount] = useState(
    initial?.amount !== null && initial?.amount !== undefined ? String(initial.amount) : '',
  )
  const [promoCode, setPromoCode] = useState(initial?.promoCode ?? '')
  const [affiliateRate, setAffiliateRate] = useState(
    initial?.affiliateRate !== null && initial?.affiliateRate !== undefined
      ? String(initial.affiliateRate)
      : '',
  )
  const [campaignId, setCampaignId] = useState(initial?.campaignId ?? '')
  const [dueDate, setDueDate] = useState(initial?.dueDate ?? '')

  const [briefFile, setBriefFile] = useState<File | null>(null)
  const [briefUploading, setBriefUploading] = useState(false)
  const [briefAttachment, setBriefAttachment] = useState<Attachment | null>(null)
  const [briefError, setBriefError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function handleBriefChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !dealId) return
    setBriefFile(file)
    setBriefUploading(true)
    setBriefError(null)
    try {
      const att = await uploadAttachment({
        file,
        kind: 'brief',
        linkedType: 'deal',
        linkedId: dealId,
      })
      setBriefAttachment(att)
    } catch (err) {
      setBriefError(err instanceof Error ? err.message : 'Erreur upload')
    } finally {
      setBriefUploading(false)
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSave({
      creatorId,
      appId,
      campaignId: campaignId || null,
      stage,
      compModel: compModel || null,
      amount: amount !== '' ? Number(amount) : null,
      currency: 'EUR',
      promoCode: promoCode.trim() || null,
      affiliateRate: affiliateRate !== '' ? Number(affiliateRate) : null,
      dueDate: dueDate || null,
    })
  }

  const showAmount = compModel === 'flat' || compModel === 'hybrid'
  const showPromoAffiliate = compModel === 'promo_affiliate' || compModel === 'hybrid'

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <label style={labelStyle}>
        <span>Étape</span>
        <select
          aria-label="Étape"
          value={stage}
          onChange={(e) => setStage(e.target.value as DealStage)}
          style={inputStyle}
          required
        >
          {DEAL_STAGES.map((s) => (
            <option key={s} value={s}>{DEAL_STAGE_LABELS[s]}</option>
          ))}
        </select>
      </label>

      <label style={labelStyle}>
        <span>Modèle de rémunération</span>
        <select
          aria-label="Modèle de rémunération"
          value={compModel}
          onChange={(e) => setCompModel(e.target.value as CompModel | '')}
          style={inputStyle}
        >
          <option value="">— Aucun —</option>
          {(Object.keys(COMP_MODEL_LABELS) as CompModel[]).map((m) => (
            <option key={m} value={m}>{COMP_MODEL_LABELS[m]}</option>
          ))}
        </select>
      </label>

      {showAmount && (
        <label style={labelStyle}>
          <span>Montant (€)</span>
          <input
            aria-label="Montant (€)"
            type="number"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            style={inputStyle}
          />
        </label>
      )}

      {showPromoAffiliate && (
        <>
          <label style={labelStyle}>
            <span>Code promo</span>
            <input
              aria-label="Code promo"
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            <span>Taux d&apos;affiliation (%)</span>
            <input
              aria-label="Taux d'affiliation"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={affiliateRate}
              onChange={(e) => setAffiliateRate(e.target.value)}
              style={inputStyle}
            />
          </label>
        </>
      )}

      <label style={labelStyle}>
        <span>Campagne</span>
        <select
          aria-label="Campagne"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Aucune —</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </label>

      <label style={labelStyle}>
        <span>Échéance</span>
        <input
          aria-label="Échéance"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          style={inputStyle}
        />
      </label>

      {/* Brief (uniquement pour un deal existant) */}
      {dealId && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <span style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontWeight: 500 }}>Brief</span>
          <label
            htmlFor="brief-upload"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.375rem 0.75rem',
              background: 'var(--elevated)',
              border: '1px solid var(--line)',
              borderRadius: '6px',
              fontSize: '0.8125rem',
              color: 'var(--muted)',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            {briefUploading
              ? 'Chargement…'
              : briefAttachment
                ? briefAttachment.filename
                : briefFile
                  ? briefFile.name
                  : 'Charger un brief'}
          </label>
          <input
            id="brief-upload"
            aria-label="Charger un brief"
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.txt,.md"
            style={{ display: 'none' }}
            onChange={(e) => { void handleBriefChange(e); }}
          />
          {briefError && (
            <span style={{ fontSize: '0.75rem', color: 'var(--arancio)' }}>{briefError}</span>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: '0.4375rem 1rem',
            background: 'transparent',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            color: 'var(--muted)',
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Annuler
        </button>
        <button
          type="submit"
          style={{
            padding: '0.4375rem 1rem',
            background: 'var(--arancio)',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Enregistrer
        </button>
      </div>
    </form>
  )
}
